import { inject, Injectable, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CookieService } from 'ngx-cookie-service';
import { MatomoTracker } from 'ngx-matomo-client';
import { CookieConsentComponent } from './../common/cookie-consent/cookie-consent.component';
import { HubspotTrackingService } from './hubspot-tracking.service';

@Injectable( {
    providedIn: 'root',
} )
export class MatomoService {

    #dialog = inject( MatDialog );
    #cookieService = inject( CookieService );
    #tracker = inject( MatomoTracker );
    #hubspotService = inject( HubspotTrackingService );

    /**
     * Observable to expose the user's consent status globally
     */
    consentChecked = signal<boolean>( false );

    constructor() {
        const hasConsent = this.#cookieService.check( 'scope.consent' );
        this.consentChecked.set( hasConsent );
    }

    /**
     * Shows the Matomo consent banner
     * @param simple If true, shows a simplified consent choice; otherwise, shows a granular choice
     */
    handleTrackingConsent( simple = true ) {
        const checked = this.#cookieService.get( 'scope.consent' ) === 'true';
        const data = { checked, simple };

        const dialogRef = this.#dialog.open( CookieConsentComponent, {
            data,
            closeOnNavigation: false,
            enterAnimationDuration: '0',
            exitAnimationDuration: '0',
        } );

        dialogRef.afterClosed().subscribe( ( consent ) => {
            if ( consent !== undefined ) {
                const expireDate = new Date();
                expireDate.setDate( expireDate.getDate() + 400 ); // Max 400 days
                this.#cookieService.set(
                    'scope.consent',
                    consent,
                    expireDate,
                    '/',           // path
                    undefined,     // domain (Browser setzt automatisch)
                    true,          // secure (wichtig für https dev!)
                    'Lax'          // SameSite
                );

                const hasConsent = this.#cookieService.check( 'scope.consent' );
                this.consentChecked.set( hasConsent );

                // HubSpot: Script laden bei Consent, Cookies entfernen bei Ablehnung
                if ( consent === true ) {
                    this.#hubspotService.loadScript();
                } else {
                    this.#hubspotService.removeConsent();
                }
            }
        } );
    }

    /**
     * Tracks a generic event in Matomo
     * @param category Category of the event
     * @param action Action of the event
     * @param name Optional name of the event
     * @param value Optional numeric value for the event
     */
    trackEvent( category: string, action: string, name?: string, value?: any ) {
        this.#tracker.trackEvent( category, action, name, value );
    }

    /**
     * Tracks a page view in Matomo
     * @param url Optional URL of the page; defaults to current path
     */
    trackPageView( url?: string ) {
        this.#tracker.trackPageView( url || window.location.pathname );
    }
}
